import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ShieldCheck, CreditCard, Zap } from 'lucide-react';
import { PlanSelector, PlanId } from './PlanSelector';
import { UrgencyBanner } from './UrgencyBanner';
interface BottomSectionProps {
  selectedPlan: PlanId;
  onSelectPlan: (id: PlanId) => void;
}
export function BottomSection({
  selectedPlan,
  onSelectPlan
}: BottomSectionProps) {
  const faqs = [{
    q: '¿Cómo recibo el kit después de comprar?',
    a: 'El acceso es inmediato. Apenas se confirma el pago por Mercado Pago, recibís un mail automático con el link para descargar todos los PDFs (los 8 tomos y los 3 bonos) a tu computadora, tablet o celular.'
  }, {
    q: '¿Sirve si soy residente o necesito más experiencia?',
    a: 'Está diseñado para los dos extremos: el residente que nunca dio una mala noticia encuentra el protocolo paso a paso, y el especialista con 20 años encuentra el sistema que nunca tuvo tiempo de armar. Las herramientas son las mismas — lo que cambia es cuánto te ahorran.'
  }, {
    q: '¿Las plantillas son editables o solo las puedo leer?',
    a: 'Los consentimientos del Bono 1 vienen en Word editable para que pongas el membrete de tu institución. Las hojas entregables y de registro están diseñadas para imprimir y fotocopiar directamente.'
  }, {
    q: '¿Los 3 bonos están incluidos en las dos ofertas?',
    a: 'Sí. Kit Esencial y Megapack incluyen los 3 bonos completos. La diferencia entre las ofertas son los Tomos 7 y 8.'
  }, {
    q: '¿Sirve si trabajo en hospital público además de la práctica privada?',
    a: 'Sí — el contenido contempla las dos realidades: PAMI, IOMA, obras sociales, trámites de traslados, derivaciones a centros de referencia y la logística del sistema argentino.'
  }, {
    q: '¿Sirve si no hago braquiterapia ni atiendo niños?',
    a: 'El Kit Esencial de 6 tomos cubre todo el ciclo del paciente adulto en RT externa. Los Tomos 7 y 8 son para quien trata (o deriva) esos casos — y el Tomo 8 incluye el protocolo del paciente derivado, útil aunque la técnica la haga otro centro.'
  }, {
    q: '¿Qué diferencia tiene con los libros de la especialidad?',
    a: 'Los libros enseñan la técnica. Esto es el sistema de comunicación: guiones con las frases exactas, hojas para entregar al paciente, consentimientos listos para imprimir. No se estudia — se usa.'
  }, {
    q: '¿Por qué elegir el Megapack y no el Kit Esencial?',
    a: 'Por $8.000 más te llevás los dos módulos donde la comunicación más falla y donde no existe ningún otro recurso en español: pediatría y técnicas avanzadas. Son los casos donde el paciente más necesita que tengas las palabras exactas.'
  }, {
    q: '¿Tiene garantía real?',
    a: 'Sí: 7 días, devolución del 100%, sin preguntas.'
  }];
  return <div className="w-full" data-id="element-8">
      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-slate-50" data-id="element-9">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16" data-id="element-11">
            <span className="text-teal-600 font-bold tracking-wider uppercase text-xs sm:text-sm mb-2 block" data-id="element-12">
              DUDAS COMUNES
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900" data-id="element-13">
              PREGUNTAS FRECUENTES
            </h2>
          </div>

          <div className="space-y-3 sm:space-y-4" data-id="element-14">
            {faqs.map((faq, idx) => <FAQItem key={idx} question={faq.q} answer={faq.a} data-id="element-15" />)}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-17">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6 sm:mb-10" data-id="element-18">
            No dejes pasar esta oportunidad
          </h2>

          <div className="mb-8 sm:mb-10" data-id="element-19">
            <UrgencyBanner data-id="element-20" />
          </div>

          <div className="mb-8 sm:mb-10" data-id="element-21">
            <PlanSelector selected={selectedPlan} onSelect={onSelectPlan} data-id="element-22" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-slate-400 font-medium" data-id="element-23">
            <div className="flex items-center gap-1.5" data-id="element-24">
              <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-400" data-id="element-25" />{' '}
              Pago seguro
            </div>
            <div className="flex items-center gap-1.5" data-id="element-26">
              <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-400" data-id="element-27" /> Acceso
              en 5 min
            </div>
            <div className="flex items-center gap-1.5" data-id="element-28">
              <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-400" data-id="element-29" />{' '}
              Garantía 7 días
            </div>
            <div className="flex items-center gap-1.5" data-id="element-30">
              <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-400" data-id="element-31" />{' '}
              Mercado Pago
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8 sm:py-12 border-t border-slate-800" data-id="element-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-33">
          <h3 className="text-white font-bold text-base sm:text-lg mb-3 sm:mb-4 tracking-wide" data-id="element-34">
            🩺 MEGAPACK DE COMUNICACIÓN ONCOLÓGICA PARA EL RADIOTERAPEUTA™
          </h3>
          <p className="max-w-2xl mx-auto mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base" data-id="element-35">
            El sistema de comunicación N°1 para oncólogos radioterapeutas en
            Argentina. Porque comunicar con seguridad no debería costarte 40
            minutos por consulta ni llamadas a toda hora.
          </p>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 text-xs sm:text-sm mb-6 sm:mb-8" data-id="element-36">
            <a href="mailto:contacto@solucionesya.shop" className="hover:text-teal-400 transition-colors" data-id="element-37">
              contacto@solucionesya.shop
            </a>
            <span className="hidden sm:inline" data-id="element-38">·</span>
            <span className="hover:text-teal-400 transition-colors cursor-pointer" data-id="element-39">
              @solucionesya.shop
            </span>
            <span className="hidden sm:inline" data-id="element-40">·</span>
            <span className="hover:text-teal-400 transition-colors cursor-pointer" data-id="element-41">
              solucionesya.shop
            </span>
          </div>
          <p className="text-[10px] sm:text-xs text-slate-600" data-id="element-42">
            © solucionesya.shop™ 2026 — Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className={`relative bg-white border rounded-xl overflow-hidden transition-colors ${isOpen ? 'border-teal-500 bg-teal-50/30' : 'border-slate-200'}`} data-id="element-43">
      <div className={`absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300 ${isOpen ? 'bg-teal-500' : 'bg-transparent'}`} data-id="element-44" />
      <motion.button whileTap={{
      scale: 0.99
    }} onClick={() => setIsOpen(!isOpen)} className="w-full px-4 py-4 sm:px-6 sm:py-5 flex items-center justify-between text-left focus:outline-none hover:bg-slate-50 transition-colors" aria-expanded={isOpen} data-id="element-45">
        <span className="font-bold text-sm sm:text-base text-slate-900 pr-4" data-id="element-46">
          {question}
        </span>
        <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-teal-600' : ''}`} data-id="element-47" />
      </motion.button>

      <AnimatePresence data-id="element-48">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-49">
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-1 sm:pt-2 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100/50" data-id="element-50">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}